import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    Box,
    Chip,
    Button,
    Stack,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { SCUIButton, SCUIDateRangerV2, SCUILoadingLinear } from '../../atomic/atoms';
import SCUIDownloadButton from '../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { SCUIContent } from '../../atomic/organisms';
import { ReturnQuery } from '../../services/query';
import { ReturnCommand } from '../../services/command';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUICustomDataGrid } from '../../atomic/molecules';
import TimeHelper from '../../utils/time';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { format, isValid, parse, subDays } from 'date-fns';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import { SRS } from './storeReturnSearch.classes';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        fontSize: 13,
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontSize: 13 },
    '& .MuiInputBase-input': { fontSize: 13 },
};

const getStatusChipSx = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('tamamland') || s.includes('complet'))
        return { bg: 'rgba(46,125,50,0.08)', color: '#2e7d32', border: 'rgba(46,125,50,0.25)' };
    if (s.includes('iptal') || s.includes('cancel'))
        return { bg: 'rgba(211,47,47,0.08)', color: '#d32f2f', border: 'rgba(211,47,47,0.25)' };
    if (s.includes('bekle') || s.includes('wait') || s.includes('pending'))
        return { bg: 'rgba(237,108,2,0.08)', color: '#e65100', border: 'rgba(237,108,2,0.25)' };
    return { bg: 'rgba(25,118,210,0.07)', color: '#1565c0', border: 'rgba(25,118,210,0.2)' };
};

const DATE_DISPLAY_FORMAT = 'dd/MM/yyyy';
const DATE_PICKER_FORMAT = 'DD/MM/YYYY';

const toApiDate = (date: string) => {
    if (!date) {
        return date;
    }

    const parsed = parse(date, DATE_DISPLAY_FORMAT, new Date());
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : date;
};

export const StoreReturnSearch: FC<any> = () => {
    const storeCodeInfo: any = localStorage.getItem('storeCode');
    const { t } = useTranslation();

    const today = format(new Date(), DATE_DISPLAY_FORMAT);
    const oneWeekAgo = format(subDays(new Date(), 7), DATE_DISPLAY_FORMAT);

    const [returnCode, setReturnCode] = useState<string>('');
    const [startDate, setStartDate] = useState<any>(oneWeekAgo);
    const [endDate, setEndDate] = useState<any>(today);
    const [statusId, setStatusId] = useState<any[]>([]);
    const [storeCode, setStoreCode] = useState<any>('');
    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(0);
    const [rowCount, setRowCount] = useState(0);
    const [location, setLocation] = useState(storeCodeInfo ? 2 : -1);
    const [returnStatus, setReturnStatus] = useState<any>();
    const [datas, setDatas] = useState<any>();
    const [show, setShow] = useState(0);
    const [loading, setLoading] = useState(false);
    const other_link: any = import.meta.env.VITE_APPURL!;

    useEffect(() => {
        GetReturnStatus();
    }, []);

    const GetReturnStatus = () => {
        ReturnQuery.GetReturnStatus().then((res: any) => {
            setReturnStatus(res.data);
        });
    };
    const _handlChangeDate = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const buildSearchParams = (take: number, skip: number) => {
        const params: any = {};

        if (returnCode) {
            params.rmaCode = returnCode;
        }
        if (startDate) {
            params.returnDateStart = toApiDate(startDate);
        }
        if (endDate) {
            params.returnDateEnd = toApiDate(endDate);
        }
        if (storeCodeInfo?.length > 0) {
            params.storeCode = storeCodeInfo;
        } else if (storeCode) {
            params.storeCode = storeCode;
        }
        if (statusId && statusId.length > 0) {
            params.returnStatusIds = statusId;
        }
        if (location !== -1) {
            params.returnLocation = location;
        }

        params.take = take;
        params.skip = skip;

        return params;
    };

    const isFilterValid = (params: any) => {
        if (
            startDate === null &&
            endDate === null &&
            (!params.storeCode || params.storeCode.length === 0) &&
            (!statusId || statusId.length === 0) &&
            location === -1
        ) {
            return false;
        }
        return true;
    };

    const mapToExcelRows = (data: any[]) =>
        data.map((item: any) => ({
            returnDate: TimeHelper.formatDateHourSecond(item.returnDate) || ' - ',
            orderDeliveredDate: TimeHelper.formatDateHourSecond(item.orderDeliveredDate) || '-',
            orderDate: TimeHelper.formatDateHourSecond(item.orderDate) || ' - ',
            returnId: item.returnId || ' - ',
            rmaCode: item.rmaCode?.toString() || ' - ',
            orderId: item.orderId || ' - ',
            orderNumber: item.orderNumber || ' ',
            cityName: item.cityName || ' ',
            orderStatusName: item.returnStatusDescription || ' - ',
            operator: item.operatorName || ' - ',
            barcode: item.barcode && item.barcode.length > 0 ? item.barcode.join(', ') : ' - ',
            storeCode: item.storeCode || ' - ',
        }));

    const DateCell = ({ value }: { value: string }) => {
        if (!value || value.trim() === '-')
            return (
                <Typography fontSize={12} color="text.secondary" className={SRS.cellDateEmpty}>
                    —
                </Typography>
            );
        const parts = value.split(' ');
        return (
            <Box className={SRS.cellDate}>
                <Typography
                    fontSize={12}
                    fontWeight={600}
                    color="text.primary"
                    lineHeight={1.4}
                    className={SRS.cellDateDay}
                >
                    {parts[0]}
                </Typography>
                {parts[1] && (
                    <Typography fontSize={11} color="text.secondary" lineHeight={1.3} className={SRS.cellDateTime}>
                        {parts[1]}
                    </Typography>
                )}
            </Box>
        );
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'returnDate',
            headerName: 'İade Tarihi',
            flex: 1,
            minWidth: 100,
            headerAlign: 'center',
            align: 'left',
            renderCell: (params: any) => <DateCell value={params.value} />,
        },
        {
            field: 'orderDeliveryDate',
            headerName: 'Sipariş Teslim Tarihi',
            flex: 1,
            minWidth: 110,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => <DateCell value={params.value} />,
        },
        {
            field: 'orderDate',
            headerName: 'Sipariş Tarihi',
            flex: 1,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => <DateCell value={params.value} />,
        },
        {
            field: 'returnId',
            headerName: t('return.returnId'),
            flex: 1,
            minWidth: 80,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <Typography
                    fontSize={13}
                    fontWeight={700}
                    color="text.primary"
                    className={SRS.cellReturnId}
                    data-return-id={params.row.returnId}
                >
                    {params.row.returnId}
                </Typography>
            ),
        },
        {
            field: 'returnNo',
            headerName: 'İade No',
            flex: 1,
            minWidth: 110,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <Tooltip title={`İade detayını aç: ${params.row.returnNo}`} placement="top" arrow>
                    <Typography
                        fontSize={13}
                        fontWeight={700}
                        className={`${SRS.cellReturnNo} ${SRS.cellReturnNoLink}`}
                        data-return-no={params.row.returnNo}
                        sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: 3,
                            transition: 'color 0.15s',
                            '&:hover': { color: 'primary.dark' },
                        }}
                        onClick={async () => {
                            const customerId = await getCustomerId(params.row.returnNo);
                            if (customerId) {
                                window.open(
                                    other_link +
                                        '/returnDetail/?ReturnId=' +
                                        params.row.returnId +
                                        '&customerId=' +
                                        customerId,
                                    '_blank'
                                );
                            }
                        }}
                    >
                        {params.row.returnNo}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'orderId',
            headerName: t('general.orderId'),
            flex: 1,
            minWidth: 80,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <Typography
                    fontSize={13}
                    color="text.secondary"
                    className={SRS.cellOrderId}
                    data-order-id={params.row.orderId}
                >
                    {params.row.orderId}
                </Typography>
            ),
        },
        {
            field: 'orderNo',
            headerName: t('general.orderNo'),
            flex: 1,
            minWidth: 110,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <Tooltip title={`Sipariş detayını aç: ${params.row.orderNo}`} placement="top" arrow>
                    <Typography
                        fontSize={13}
                        fontWeight={700}
                        className={`${SRS.cellOrderNo} ${SRS.cellOrderNoLink}`}
                        data-order-no={params.row.orderNo}
                        sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: 3,
                            transition: 'color 0.15s',
                            '&:hover': { color: 'primary.dark' },
                        }}
                        onClick={() => {
                            window.open(other_link + '/orderdetail?OrderNumber=' + params.row.orderNo, '_blank');
                        }}
                    >
                        {params.row.orderNo}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'cityName',
            headerName: t('general.cityName'),
            flex: 1,
            minWidth: 80,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <Typography fontSize={13} color="text.primary" className={SRS.cellCity}>
                    {params.row.cityName}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Durum',
            flex: 1,
            minWidth: 110,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => {
                const c = getStatusChipSx(params.row.status);
                return (
                    <Tooltip title={params.row.status} placement="top" arrow>
                        <Chip
                            label={params.row.status}
                            size="small"
                            className={`${SRS.cellStatus} ${SRS.cellStatusChip}`}
                            data-status={params.row.status}
                            sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                height: 22,
                                backgroundColor: c.bg,
                                color: c.color,
                                border: `1px solid ${c.border}`,
                                maxWidth: 120,
                            }}
                        />
                    </Tooltip>
                );
            },
        },
        {
            field: 'operator',
            headerName: 'Operator',
            flex: 1,
            minWidth: 90,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <Typography fontSize={12} color="text.secondary" className={SRS.cellOperator}>
                    {params.row.operator}
                </Typography>
            ),
        },
        {
            field: 'barcode',
            headerName: t('return.barcodes'),
            flex: 1,
            minWidth: 140,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => {
                const barcodes =
                    params.row.barcode?.split(',').filter((b: string) => b.trim() && b.trim() !== '-') || [];
                if (barcodes.length === 0)
                    return (
                        <Typography fontSize={12} color="text.disabled" className={SRS.cellBarcodeEmpty}>
                            —
                        </Typography>
                    );
                return (
                    <Tooltip title="Barkodları görüntüle" placement="top" arrow>
                        <Box
                            className={`${SRS.cellBarcode} ${SRS.cellBarcodeOpen}`}
                            sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}
                            onClick={() => {
                                setSelectedBarcodeData(params.row.barcode.split(','));
                                setPopupOpen(true);
                            }}
                        >
                            {barcodes.slice(0, 2).map((barcode: string, index: number) => (
                                <Typography
                                    key={index}
                                    fontSize={11}
                                    className={SRS.cellBarcodeItem}
                                    data-barcode={barcode.trim()}
                                    sx={{
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                        textDecorationStyle: 'dotted',
                                        textUnderlineOffset: 2,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {barcode.trim()}
                                </Typography>
                            ))}
                            {barcodes.length > 2 && (
                                <Typography fontSize={10} color="text.disabled" className={SRS.cellBarcodeMore}>
                                    +{barcodes.length - 2} daha
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'storeCode',
            headerName: t('general.storeName'),
            flex: 1,
            minWidth: 80,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <Typography
                    fontSize={12}
                    color="text.secondary"
                    className={SRS.cellStoreCode}
                    data-store-code={params.row.storeCode}
                >
                    {params.row.storeCode}
                </Typography>
            ),
        },
    ];

    const StoreReturnSearchList = () => {
        setLoading(true);
        const params = buildSearchParams(pageSize, pageNumber);

        if (!isFilterValid(params)) {
            Swal.fire('Filtre değerlerinden en az birini seçmeniz gerekmektedir.', ' ', 'error');
            setLoading(false);
            return;
        }

        ReturnCommand.SearchStoreReturns(params)
            .then((res: any) => {
                if (res?.data && res.data.length > 0) {
                    const parseData = res.data.map((item: any) => {
                        return {
                            returnDate: TimeHelper.formatDateHourSecond(item.returnDate) || ' - ',
                            orderDeliveryDate: TimeHelper.formatDateHourSecond(item.orderDeliveredDate) || '-',
                            orderDate: TimeHelper.formatDateHourSecond(item.orderDate) || ' - ',
                            returnId: item.returnId || ' - ',
                            returnNo: item.rmaCode.toString() || ' - ',
                            orderId: item.orderId || ' - ',
                            orderNo: item.orderNumber || ' ',
                            cityName: item.cityName || ' ',
                            status: item.returnStatusDescription || ' - ',
                            operator: item.operatorName || ' - ',
                            barcode: item.barcode && item.barcode.length > 0 ? item.barcode.join(', ') : ' - ',
                            storeCode: item.storeCode || ' - ',
                        };
                    });
                    setDatas(parseData);
                    setRowCount(res.count);
                } else {
                    setDatas([]);
                    setRowCount(0);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleExcelDownload = async (): Promise<any[]> => {
        const params = buildSearchParams(rowCount > 0 ? rowCount : pageSize, 0);

        try {
            const res = await ReturnCommand.SearchStoreReturns(params);
            if (res?.data && res.data.length > 0) {
                return mapToExcelRows(res.data);
            }
            return [];
        } catch (error: any) {
            console.log('Hata :>> ', error);
            return [];
        }
    };

    useEffect(() => {
        if (show === 1) {
            StoreReturnSearchList();
        }
    }, [pageSize, pageNumber]);

    const getCustomerId = async (RMACode: string) => {
        setLoading(true);
        try {
            if (isNaN(Number(RMACode))) {
                throw new Error('RMACode yalnızca sayısal karakterler içermelidir.');
            }
            const longRMACode = parseInt(RMACode, 10);
            let req = {
                rmaCode: longRMACode,
                shippingListId: 0,
                saleOrderId: 0,
                saleOrderNumber: '',
                shippingListNo: null,
            };
            const res = await ReturnCommand.PostGetReturnList(req);
            if (res.returnModels && res.returnModels.length > 0) {
                let parseData = res.returnModels;
                if (parseData.length === 1 && parseData[0].customerId) {
                    return parseData[0].customerId;
                }
            } else {
                setDatas([]);
            }
        } catch (error) {
            setDatas([]);
            console.log('Hata :>> ', error);
        } finally {
            setLoading(false);
        }
        return null;
    };

    const [selectedBarcodeData, setSelectedBarcodeData] = useState<any>();
    const [popupOpen, setPopupOpen] = useState(false);

    const handleClosePopup = () => {
        setSelectedBarcodeData([]);
        setPopupOpen(false);
    };

    return (
        <SCUIContent className={SRS.page}>
            <Box className={SRS.root}>
                {/* Barcode Dialog */}
                {popupOpen && selectedBarcodeData?.length > 0 && (
                    <Dialog
                        open={popupOpen}
                        onClose={handleClosePopup}
                        className={SRS.barcodeDialog}
                        slotProps={{
                            paper: {
                                sx: { borderRadius: 3, minWidth: 320, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
                            },
                        }}
                    >
                        <DialogTitle
                            className={SRS.barcodeDialogTitle}
                            sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <QrCodeScannerIcon
                                className={SRS.barcodeDialogTitleIcon}
                                sx={{ fontSize: 20, color: 'primary.main' }}
                            />
                            <Typography fontWeight={700} fontSize={15} className={SRS.barcodeDialogTitleText}>
                                Barkod Detayları
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <CloseIcon
                                className={SRS.barcodeDialogCloseIcon}
                                sx={{
                                    fontSize: 18,
                                    color: 'grey.400',
                                    cursor: 'pointer',
                                    '&:hover': { color: 'grey.700' },
                                }}
                                onClick={handleClosePopup}
                            />
                        </DialogTitle>
                        <DialogContent className={SRS.barcodeDialogContent} sx={{ pt: 1 }}>
                            <Stack spacing={0.75} className={SRS.barcodeDialogList}>
                                {selectedBarcodeData.map((barcode: any, index: any) => (
                                    <Chip
                                        key={index}
                                        icon={<QrCodeScannerIcon />}
                                        label={barcode.trim()}
                                        size="small"
                                        className={SRS.barcodeDialogChip}
                                        data-barcode={barcode.trim()}
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            backgroundColor: 'rgba(25,118,210,0.06)',
                                            color: 'text.primary',
                                            border: '1px solid rgba(25,118,210,0.2)',
                                            justifyContent: 'flex-start',
                                            '& .MuiChip-icon': { fontSize: 15, color: 'primary.main' },
                                        }}
                                    />
                                ))}
                            </Stack>
                        </DialogContent>
                        <DialogActions className={SRS.barcodeDialogActions} sx={{ px: 2.5, pb: 2 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClosePopup}
                                className={SRS.btnDialogClose}
                                sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12 }}
                            >
                                Kapat
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}

                {/* Page Header */}
                <Box
                    className={SRS.header}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, mt: -3 }}
                >
                    <AssignmentReturnIcon className={SRS.headerIcon} sx={{ fontSize: 22, color: 'primary.main' }} />
                    <Typography
                        fontWeight={800}
                        fontSize={18}
                        color="text.primary"
                        letterSpacing={0.2}
                        className={SRS.headerTitle}
                    >
                        {t('general.storeReturnStatusReport')}
                    </Typography>
                </Box>

                <Box className={SRS.loading}>
                    <SCUILoadingLinear display={loading} />
                </Box>

                {/* Filter Bar */}
                <Paper
                    elevation={0}
                    className={SRS.filterBar}
                    sx={{
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 3,
                        px: 2.5,
                        py: 2,
                        mb: 2,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                    }}
                >
                    <Grid container spacing={1.5} alignItems="center" className={SRS.filterGrid}>
                        {/* Return Code */}
                        <Grid size={{ xl: 2, lg: 2, md: 6, xs: 12 }} className={SRS.filterReturnCode}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('general.customerReturnCode')}
                                placeholder={t('general.customerReturnCode')}
                                className={SRS.inputReturnCode}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AssignmentReturnIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                onChange={(event: any) => {
                                    setReturnCode(event.target.value.trim());
                                }}
                                sx={inputSx}
                            />
                        </Grid>

                        {/* Store Code */}
                        <Grid size={{ xl: 1.5, lg: 1.5, md: 6, xs: 12 }} className={SRS.filterStoreCode}>
                            {storeCodeInfo?.length === 0 ? (
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t('general.storeCode')}
                                    placeholder={t('general.storeCode')}
                                    className={SRS.inputStoreCode}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <StorefrontIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    onChange={(event: any) => {
                                        setStoreCode(event.target.value.trim());
                                    }}
                                    sx={inputSx}
                                />
                            ) : (
                                <TextField
                                    disabled
                                    fullWidth
                                    size="small"
                                    label={storeCodeInfo}
                                    className={SRS.inputStoreCode}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <StorefrontIcon sx={{ fontSize: 16, color: 'grey.300' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    sx={inputSx}
                                />
                            )}
                        </Grid>

                        {/* Date Range */}
                        <Grid
                            size={{ xl: 2.8, lg: 2.8, md: 12, xs: 12 }}
                            className={`${SRS.filterDateRange} ${SRS.inputDateRange}`}
                        >
                            <SCUIDateRangerV2
                                size="small"
                                setDateRange={_handlChangeDate}
                                dateFormat={DATE_PICKER_FORMAT}
                                fromDate={startDate}
                                toDate={endDate}
                            />
                        </Grid>

                        {/* Status Multi-Select */}
                        <Grid size={{ xl: 2, lg: 2, md: 6, xs: 12 }} className={SRS.filterStatus}>
                            <FormControl fullWidth size="small" sx={inputSx}>
                                <InputLabel>{t('comments.selectStatus')}</InputLabel>
                                <Select
                                    fullWidth
                                    multiple
                                    label={t('comments.selectStatus')}
                                    value={statusId}
                                    className={SRS.inputStatus}
                                    onChange={(event: any) => setStatusId(event.target.value)}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) return '';
                                        return returnStatus
                                            ?.filter((item: any) => selected.includes(item.returnStatusId))
                                            .map((item: any) => item.statusDescription)
                                            .join(', ');
                                    }}
                                    MenuProps={{
                                        PaperProps: { style: { maxHeight: 48 * 4.5 + 8 } },
                                    }}
                                >
                                    {returnStatus?.map((item: any) => (
                                        <MenuItem
                                            key={item.returnStatusId}
                                            id={item.returnStatusId}
                                            value={item.returnStatusId}
                                            className={SRS.statusMenuItem}
                                            data-status-id={item.returnStatusId}
                                            sx={{ fontSize: 13 }}
                                        >
                                            <Checkbox
                                                checked={statusId.indexOf(item.returnStatusId) > -1}
                                                size="small"
                                                className={SRS.statusMenuCheckbox}
                                                sx={{ p: 0.5, mr: 1 }}
                                            />
                                            <ListItemText
                                                primary={item.statusDescription}
                                                className={SRS.statusMenuLabel}
                                                slotProps={{
                                                    primary: { fontSize: 13 },
                                                }}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Location Select */}
                        <Grid size={{ xl: 1.6, lg: 1.6, md: 6, xs: 12 }} className={SRS.filterLocation}>
                            <FormControl fullWidth size="small" sx={inputSx}>
                                <InputLabel>{t('general.transactionLocation')}</InputLabel>
                                <Select
                                    labelId="statusSelectLabel"
                                    fullWidth
                                    id="statusSelect"
                                    value={location}
                                    label={t('general.transactionLocation')}
                                    className={SRS.inputLocation}
                                    onChange={(event: any) => setLocation(event.target.value)}
                                    disabled={!!storeCodeInfo}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <PlaceIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                                        </InputAdornment>
                                    }
                                    MenuProps={{
                                        PaperProps: { style: { maxHeight: 48 * 4.5 + 8 } },
                                    }}
                                >
                                    <MenuItem
                                        value={-1}
                                        className={SRS.locationMenuItem}
                                        data-location-value={-1}
                                        sx={{ fontSize: 13 }}
                                    >
                                        {t('general.all')}
                                    </MenuItem>
                                    <MenuItem
                                        value={1}
                                        className={SRS.locationMenuItem}
                                        data-location-value={1}
                                        sx={{ fontSize: 13 }}
                                    >
                                        {t('retail.warehouse')}
                                    </MenuItem>
                                    <MenuItem
                                        value={2}
                                        className={SRS.locationMenuItem}
                                        data-location-value={2}
                                        sx={{ fontSize: 13 }}
                                    >
                                        {t('general.store')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filter Button */}
                        <Grid size={{ xl: 'auto', lg: 'auto', md: 12, xs: 12 }} className={SRS.filterAction}>
                            <SCUIButton
                                text={t('general.filter')}
                                type="primary"
                                sizeType="fixed"
                                buttonType="submit"
                                size="m"
                                showStartIcon
                                startIcon={<SearchIcon style={{ fontSize: '15px' }} />}
                                className={SRS.btnFilter}
                                style={{ height: '37px', borderRadius: '8px', fontWeight: 700, whiteSpace: 'nowrap' }}
                                onClick={() => {
                                    setPageSize(10);
                                    setPageNumber(0);
                                    StoreReturnSearchList();
                                    setShow(1);
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Data Grid */}
                {datas && show === 1 && (
                    <Box className={SRS.grid}>
                        {Array.isArray(datas) && datas.length > 0 && (
                            <SCUIDownloadButton
                                position="right"
                                excelData={{
                                    excelDownloadData: [],
                                    excelDownloadFolderName: 'StoreReturnSearch',
                                    excelDownloadButtonText: t('general.exportToExcel'),
                                }}
                                onclick={handleExcelDownload}
                            />
                        )}
                        <SCUICustomDataGrid
                            randomId
                            rows={datas}
                            columns={columns}
                            onPageSizeChange={(i: number) => {
                                setPageSize(i);
                                setPageNumber(0);
                            }}
                            pageSize={pageSize}
                            paginationModeChange
                            onPageChange={(i: number) => {
                                setPageNumber(i);
                            }}
                            page={pageNumber}
                            rowCount={rowCount}
                            sx={{
                                border: 0,
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'grey.50',
                                    fontWeight: 700,
                                    fontSize: 12,
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: 'rgba(25,118,210,0.03)',
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        </SCUIContent>
    );
};
